import { useRef, type ReactNode } from 'react'
import type { Id } from '@/infra/id'
import cx from 'classnames'
import styles from './ConversationComposer.module.css'
import { Icon } from '@/components/Icon'
import TextareaAutosize from 'react-textarea-autosize'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownDivider,
  DropdownScroll,
} from '@/components/Dropdown'
import { OneLine } from '@/components/OneLine'
import { IconButton } from '@/components/IconButton'
import { RawButton } from '@/components/Button'
import { Tooltip } from '@/components/Tooltip'
import { FileButton } from '@/components/FileButton'
import { useToggle } from '@/hooks/state-hooks'

export interface ConversationComposerProps {
  /**
   * Actions shown before the input, e.g. a {@link ConversationComposerAddMenu}.
   */
  leadingActions?: ReactNode
  canToggleNoteMode: boolean
  /**
   * Shown in place of the note mode toggle when the composer has no note mode.
   */
  trailingActions?: ReactNode
  isNoteModeEnabled: boolean
  onToggleNoteMode: () => unknown
  composerText: string
  onComposerTextChange: (text: string) => unknown
  onSend: () => unknown
}

export function ConversationComposer({
  leadingActions,
  canToggleNoteMode,
  trailingActions,
  isNoteModeEnabled,
  onToggleNoteMode,
  composerText,
  onComposerTextChange,
  onSend,
}: ConversationComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  return (
    <div className={styles.root} data-slot="composer-root">
      <div
        className={cx(styles.composer, isNoteModeEnabled && styles.noteMode)}
        data-slot="composer"
      >
        {leadingActions && (
          <div className={styles.addContainer} data-slot="composer-add">
            {leadingActions}
          </div>
        )}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Only widens the textarea's click target; the textarea itself is keyboard-focusable. */}
        <div
          className={styles.inputContainer}
          data-slot="composer-input-container"
          onClick={() => textareaRef.current?.focus()}
        >
          <TextareaAutosize
            className={cx(styles.input, composerText && styles.filled)}
            data-slot="composer-input"
            ref={textareaRef}
            data-private
            data-test="conversation-input"
            // Have to use style here because the library adds it itself.
            style={{ resize: 'none' }}
            placeholder={
              (isNoteModeEnabled
                ? "Write your note here. Clients can't see notes. "
                : 'Write your message here. ') +
              'Shift + Enter to add a new line.'
            }
            value={composerText}
            maxRows={20}
            onChange={(e) =>
              onComposerTextChange((e.target as HTMLTextAreaElement).value)
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onSend()
              }
            }}
          />
        </div>
        <div className={styles.extrasContainer}>
          {canToggleNoteMode ? (
            <Tooltip
              placement="top"
              content={
                isNoteModeEnabled
                  ? 'Click to switch to Message'
                  : 'Click to switch to Note'
              }
            >
              <IconButton
                name="chat-note"
                label={
                  isNoteModeEnabled ? 'Switch to message' : 'Switch to note'
                }
                variant="ghost"
                onPress={onToggleNoteMode}
                size={45}
                color={isNoteModeEnabled ? 'var(--purple700)' : undefined}
              />
            </Tooltip>
          ) : (
            trailingActions
          )}
          <IconButton
            name="chat-submit"
            label={isNoteModeEnabled ? 'Add note' : 'Send message'}
            variant="ghost"
            isDisabled={!composerText.trim()}
            onPress={() => onSend()}
            testId="conversation-send"
            size={45}
            color={isNoteModeEnabled ? 'var(--purple700)' : undefined}
          />
        </div>
      </div>
    </div>
  )
}

export interface ComposerDocumentType {
  id: Id
  name: string
}

export interface ConversationComposerAddMenuProps {
  documentTypes: ComposerDocumentType[]
  onUpload: (files: FileList, typeId: Id) => unknown
  canUploadOtherDocuments: boolean
  onUploadOtherDocuments: (files: FileList) => unknown
  showDocumentTab(): void
  onLinkExisting: () => unknown
  showMessageBank: boolean
  onMessageBank: () => unknown
}

/**
 * The composer's menu for adding documents and message bank replies. Render it
 * in the composer's `leadingActions`, whose note mode restyles its button.
 */
export function ConversationComposerAddMenu({
  documentTypes,
  onUpload,
  canUploadOtherDocuments,
  onUploadOtherDocuments,
  showDocumentTab,
  onLinkExisting,
  showMessageBank,
  onMessageBank,
}: ConversationComposerAddMenuProps) {
  const dropdownOpened = useToggle(false)
  const toggle = dropdownOpened.toggle

  return (
    <Dropdown
      position="top-start"
      isOpen={dropdownOpened.isEnabled}
      onRequestClose={dropdownOpened.disable}
      button={
        <RawButton
          onPress={toggle}
          className={cx(
            styles.addButton,
            dropdownOpened.isEnabled && styles.dropdownOpen,
          )}
          data-slot="composer-add-button"
        >
          <Icon name="add" />
        </RawButton>
      }
      testId="chat-add-document-dropdown-new"
    >
      <DropdownTitle>Add new</DropdownTitle>
      <DropdownScroll>
        {documentTypes.map((type) => (
          <DropdownItem
            onPress={() => showDocumentTab()}
            key={type.id}
            relative
          >
            <OneLine>
              <FileButton
                overlay
                onChange={(files: FileList) => {
                  onUpload(files, type.id)
                  toggle()
                }}
                data-test={`chat-upload-document-input-${type.name}`}
              />
              {type.name}
            </OneLine>
          </DropdownItem>
        ))}
        {canUploadOtherDocuments && (
          <DropdownItem onPress={() => showDocumentTab()} relative>
            <OneLine>
              <FileButton
                overlay
                multiple={true}
                onChange={(files: FileList) => {
                  onUploadOtherDocuments(files)
                  toggle()
                }}
                data-test="chat-upload-document-input-new"
              />
              Other documents
            </OneLine>
          </DropdownItem>
        )}
      </DropdownScroll>
      <DropdownDivider />
      <DropdownTitle>Actions</DropdownTitle>
      <DropdownItem
        onPress={() => {
          onLinkExisting()
          toggle()
        }}
        data-test="chat-add-file-link-existing-dropdown-item-new"
      >
        <OneLine>Link existing</OneLine>
      </DropdownItem>

      {showMessageBank && (
        <DropdownItem onPress={() => onMessageBank()}>
          <OneLine>Message bank reply</OneLine>
        </DropdownItem>
      )}
    </Dropdown>
  )
}
